/**
 * KAN-746: "Добавить товар" в заказ: поиск по каталогу (название или
 * артикул, с задержкой), выбор товара добавляет его в заказ количеством 1.
 * KAN-781: рядом с поиском выбор категории каталога. Товары грузятся по
 * категории (с подкатегориями) или по тексту, список постраничный: кнопка
 * "Показать ещё", пока есть следующая страница. Смена категории или текста
 * начинает с первой страницы. Панель после добавления остаётся открытой.
 */
import { useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { ProductThumb } from '@/components/catalog/ProductThumb';
import { useCatalogCategoryOptions, useCatalogProductsPages } from '@/hooks/useCatalog';
import { useDebounce } from '@/hooks/useDebounce';
import { formatPrice } from '@/lib/catalogTree';
import { PRODUCT_PICKER_PAGE_SIZE, flattenProductPages, productQueryParams } from '@/lib/orderProductPicker';
import type { CatalogId } from '@/types/catalog';

export const PRODUCT_SEARCH_DEBOUNCE_MS = 300;
const ALL_CATEGORIES = { value: '', label: 'Все категории' };

interface AddOrderProductProps {
  onAdd: (productId: CatalogId) => void;
  pending?: boolean;
}

export function AddOrderProduct({ onAdd, pending = false }: AddOrderProductProps) {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('');
  const [input, setInput] = useState('');
  const search = useDebounce(input.trim(), PRODUCT_SEARCH_DEBOUNCE_MS);
  const categories = useCatalogCategoryOptions(open);
  const query = productQueryParams({ category, search, pageSize: PRODUCT_PICKER_PAGE_SIZE });
  const products = useCatalogProductsPages(query ?? {}, open && query !== null);
  const results = flattenProductPages(products.data?.pages);
  const canLoadMore = products.hasNextPage && !products.isPlaceholderData;

  const close = () => {
    setOpen(false);
    setCategory('');
    setInput('');
  };

  if (!open) {
    return (
      <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="order-add-product">
        <Plus className="mr-1 h-4 w-4" />
        Добавить товар
      </Button>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-lg border bg-gray-50 p-3" data-testid="order-add-product-panel">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <div className="sm:w-56">
          <NativeSelect
            aria-label="Категория"
            data-testid="order-add-category"
            options={[ALL_CATEGORIES, ...categories]}
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
        </div>
        <div className="flex flex-1 items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="search"
              autoFocus
              aria-label="Поиск товара в каталоге"
              placeholder="Название или артикул"
              className="bg-white pl-9"
              value={input}
              onChange={(e) => setInput(e.target.value)}
            />
          </div>
          <Button type="button" variant="ghost" size="sm" onClick={close}>
            Закрыть
          </Button>
        </div>
      </div>
      {query === null ? (
        <p className="text-sm text-gray-500">Выберите категорию или введите название</p>
      ) : products.isLoading ? (
        <p className="text-sm text-gray-500">Поиск...</p>
      ) : products.isError ? (
        <p className="text-sm text-red-600">Не удалось загрузить каталог</p>
      ) : results.length === 0 ? (
        <p className="text-sm text-gray-500">Товары не найдены</p>
      ) : (
        <>
          <ul className="max-h-72 divide-y overflow-y-auto rounded-md border bg-white" aria-label="Найденные товары">
            {results.map((product) => (
              <li key={product.id}>
                <button
                  type="button"
                  disabled={pending}
                  data-testid={`order-add-option-${product.id}`}
                  onClick={() => onAdd(product.id)}
                  className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-blue-50 disabled:opacity-50"
                >
                  <ProductThumb src={product.picture_url} alt="" className="h-9 w-9 shrink-0" />
                  <span className="min-w-0 flex-1">
                    <span className="line-clamp-2 text-sm font-medium text-gray-900">{product.name}</span>
                    <span className="block text-xs text-gray-500">{product.sku || 'без артикула'}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap text-sm text-gray-700">
                    {product.price === null ? 'цена по запросу' : formatPrice(product.price, product.currency)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {canLoadMore && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="order-add-more"
              disabled={products.isFetchingNextPage}
              onClick={() => void products.fetchNextPage()}
            >
              {products.isFetchingNextPage ? 'Загрузка...' : 'Показать ещё'}
            </Button>
          )}
        </>
      )}
    </div>
  );
}
